<template>
	<div ref="root" class="status-select">
		<button
			ref="trigger"
			type="button"
			role="combobox"
			class="status-trigger"
			:class="{ 'is-open': isOpen }"
			:disabled="disabled"
			:aria-label="ariaLabel"
			aria-haspopup="listbox"
			:aria-expanded="isOpen"
			:aria-controls="listboxId"
			:aria-activedescendant="isOpen ? optionId(activeIndex) : undefined"
			@click="isOpen ? close() : open()"
			@keydown="handleKeydown"
		>
			<Transition name="status-value" mode="out-in">
				<span :key="modelValue || 'empty'" class="status-value">{{ modelValue || ' ' }}</span>
			</Transition>
			<svg class="status-chevron" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
				<path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
			</svg>
		</button>

		<Transition name="status-menu" :duration="{ enter: 400, leave: 160 }">
			<div
				v-if="isOpen"
				class="status-menu"
				:class="{ 'is-above': isAbove }"
				:style="menuStyle"
				@mousedown.prevent
			>
				<div class="status-surface">
					<span class="status-highlight" :style="highlightStyle" aria-hidden="true"></span>
					<ul ref="list" :id="listboxId" role="listbox" :aria-label="ariaLabel" class="status-options">
						<li
							v-for="(option, index) in options"
							:id="optionId(index)"
							:key="option || 'empty'"
							role="option"
							class="status-option"
							:class="{ 'is-selected': option === modelValue, 'is-empty': !option }"
							:style="{ '--i': isAbove ? options.length - 1 - index : index }"
							:aria-selected="option === modelValue"
							:aria-label="option ? undefined : 'No status'"
							@pointerenter="activeIndex = index"
							@click="choose(option)"
						>
							{{ option || '—' }}
						</li>
					</ul>
				</div>
			</div>
		</Transition>
	</div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onDeactivated, ref, useId, watch } from 'vue';
import { fitFloatingPanel, trackFloatingAnchor } from '@/utils/floatingPanel';

const props = defineProps({
	modelValue: { type: String, default: '' },
	options: { type: Array, required: true },
	disabled: { type: Boolean, default: false },
	ariaLabel: { type: String, default: 'Status' },
});

const emit = defineEmits(['update:modelValue', 'change']);

const MIN_WIDTH = 112;
const GAP = 4;

const id = useId();
const listboxId = `${id}-listbox`;
const optionId = (index) => `${id}-option-${index}`;

const root = ref(null);
const trigger = ref(null);
const list = ref(null);
const isOpen = ref(false);
const isAbove = ref(false);
const activeIndex = ref(0);
const menuStyle = ref({});
const highlight = ref({ top: 0, height: 0 });
let stopTracking;

const highlightStyle = computed(() => ({
	height: `${highlight.value.height}px`,
	transform: `translateY(${highlight.value.top}px)`,
}));

// Opens under the cell like the catalog lists, or above it near the bottom of the screen.
const position = () => {
	const rect = trigger.value?.getBoundingClientRect();
	if (!rect) return;
	const width = Math.max(rect.width, MIN_WIDTH);
	const height = list.value ? list.value.offsetHeight + 10 : props.options.length * 32 + 10;
	const style = fitFloatingPanel(rect, { width, height }, undefined, GAP);
	isAbove.value = parseFloat(style.top) < rect.top;
	menuStyle.value = { left: style.left, top: style.top, width: style.width };
};

// One highlight slides between the options instead of each option lighting up on its own.
const measureHighlight = () => {
	const option = list.value?.children[activeIndex.value];
	if (option) highlight.value = { top: option.offsetTop, height: option.offsetHeight };
};

watch(activeIndex, () => {
	if (isOpen.value) nextTick(measureHighlight);
});

const handleOutsidePointer = (event) => {
	if (!root.value?.contains(event.target)) close();
};

const open = () => {
	if (props.disabled || isOpen.value) return;
	activeIndex.value = Math.max(0, props.options.indexOf(props.modelValue));
	position();
	isOpen.value = true;
	document.addEventListener('pointerdown', handleOutsidePointer, true);
	nextTick(() => {
		if (!isOpen.value) return;
		position();
		measureHighlight();
		stopTracking = trackFloatingAnchor(trigger.value, position);
	});
};

function close() {
	if (!isOpen.value) return;
	isOpen.value = false;
	stopTracking?.();
	stopTracking = undefined;
	document.removeEventListener('pointerdown', handleOutsidePointer, true);
}

const choose = (option) => {
	close();
	trigger.value?.focus({ preventScroll: true });
	if (option === props.modelValue) return;
	emit('update:modelValue', option);
	emit('change', option);
};

const move = (index) => {
	activeIndex.value = Math.min(props.options.length - 1, Math.max(0, index));
};

const handleKeydown = (event) => {
	if (!isOpen.value) {
		if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
			event.preventDefault();
			open();
		}
		return;
	}
	if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
		event.preventDefault();
		move(activeIndex.value + (event.key === 'ArrowDown' ? 1 : -1));
	} else if (event.key === 'Home' || event.key === 'End') {
		event.preventDefault();
		move(event.key === 'Home' ? 0 : props.options.length - 1);
	} else if (event.key === 'Enter' || event.key === ' ') {
		event.preventDefault();
		choose(props.options[activeIndex.value]);
	} else if (event.key === 'Escape') {
		event.preventDefault();
		event.stopPropagation();
		close();
	} else if (event.key === 'Tab') {
		close();
	}
};

watch(() => props.disabled, (disabled) => {
	if (disabled) close();
});
onDeactivated(close);
onBeforeUnmount(close);
</script>

<style scoped>
.status-select {
	display: flex;
	width: 100%;
	min-width: 0;
}

/* Looks like the other cells: the value centred, a chevron on the right. */
.status-trigger {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	min-height: 40px;
	padding: 10px 2rem 10px 16px;
	box-sizing: border-box;
	overflow: hidden;
	border: none;
	border-radius: 0;
	background-color: var(--noc-background, #fff);
	color: var(--text-color);
	font: var(--ap-type-body);
	cursor: pointer;
	transition: background-color 0.1s ease;
}

.status-trigger:hover:not(:disabled),
.status-trigger.is-open {
	background-color: var(--noc-background-secondary, #fafafa);
}

.status-trigger:focus-visible {
	outline: 1px solid var(--noc-blue);
	outline-offset: -1px;
}

.status-trigger:disabled {
	cursor: default;
}

.status-value {
	white-space: nowrap;
}

.status-chevron {
	position: absolute;
	top: 50%;
	right: 0.75rem;
	width: 1rem;
	height: 1rem;
	margin-top: -0.5rem;
	color: var(--text-color-secondary, #5e6170);
	transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.status-trigger.is-open .status-chevron {
	transform: rotate(180deg);
}

/* A newly picked status rises into the cell. */
.status-value-enter-active {
	transition:
		opacity 160ms ease,
		transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.status-value-leave-active {
	transition:
		opacity 90ms ease,
		transform 90ms ease-in;
}

.status-value-enter-from {
	opacity: 0;
	transform: translateY(6px);
}

.status-value-leave-to {
	opacity: 0;
	transform: translateY(-6px);
}

/* The shadow sits on the wrapper so it follows the clipped surface as it unrolls. */
.status-menu {
	position: fixed;
	z-index: 3000;
	filter: drop-shadow(0 8px 16px rgb(16 24 40 / 10%));
}

.status-surface {
	position: relative;
	padding: 4px;
	box-sizing: border-box;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 8px;
	background-color: var(--noc-background, #fff);
	clip-path: inset(0 round 8px);
}

.status-options {
	margin: 0;
	padding: 0;
	list-style: none;
}

.status-option {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 32px;
	padding: 0 12px;
	color: var(--text-color);
	font: var(--ap-type-body);
	white-space: nowrap;
	cursor: pointer;
}

.status-option.is-empty {
	color: var(--text-color-secondary);
}

.status-option.is-selected {
	color: var(--noc-blue, #0a61c9);
	font-weight: 600;
}

.status-highlight {
	position: absolute;
	top: 0;
	right: 4px;
	left: 4px;
	border-radius: 6px;
	background-color: var(--noc-blue-05, rgba(10, 97, 201, 0.05));
	transition:
		transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1),
		height 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

/* Opening: the list unrolls from the cell and the options follow one after another. */
.status-menu-enter-active {
	transition: opacity 120ms ease;
}

.status-menu-enter-active .status-surface {
	transition: clip-path 300ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.status-menu-enter-active .status-option {
	transition:
		opacity 180ms ease,
		transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1);
	transition-delay: calc(40ms + var(--i) * 18ms);
}

.status-menu-enter-active .status-highlight {
	transition: opacity 160ms ease 140ms;
}

.status-menu-enter-from {
	opacity: 0;
}

.status-menu-enter-from .status-surface {
	clip-path: inset(0 0 100% 0 round 8px);
}

.status-menu-enter-from.is-above .status-surface {
	clip-path: inset(100% 0 0 0 round 8px);
}

.status-menu-enter-from .status-option {
	opacity: 0;
	transform: translateY(-6px);
}

.status-menu-enter-from.is-above .status-option {
	transform: translateY(6px);
}

.status-menu-enter-from .status-highlight {
	opacity: 0;
}

/* Closing rolls it back up a little faster. */
.status-menu-leave-active {
	transition: opacity 140ms ease 20ms;
}

.status-menu-leave-active .status-surface {
	transition: clip-path 160ms cubic-bezier(0.4, 0, 1, 1);
}

.status-menu-leave-to {
	opacity: 0;
}

.status-menu-leave-to .status-surface {
	clip-path: inset(0 0 45% 0 round 8px);
}

.status-menu-leave-to.is-above .status-surface {
	clip-path: inset(45% 0 0 0 round 8px);
}

@media (prefers-reduced-motion: reduce) {
	.status-chevron,
	.status-highlight,
	.status-value-enter-active,
	.status-value-leave-active,
	.status-menu-enter-active,
	.status-menu-enter-active .status-surface,
	.status-menu-enter-active .status-option,
	.status-menu-enter-active .status-highlight,
	.status-menu-leave-active,
	.status-menu-leave-active .status-surface {
		transition: none;
	}
}
</style>
